import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { toast } from "react-toastify";
import { FaInfoCircle } from "react-icons/fa";
import { getParsedLocalStorageItem } from "./components/Navbar/Navbar";

import axios from "axios";
// import { trackViewContent } from './App';

import {
  localStorageGetItem,
  localStorageRemoveItem,
  localStorageSetItem,
} from "./App";

export const AppContext = createContext();

const getInitialCart = () => {
  try {
    // Retrieve the cart from local storage
    const storedCart = localStorageGetItem("cartItems");
    const parsedCart = storedCart ? JSON.parse(storedCart) : [];

    if (Array.isArray(parsedCart)) {
      return parsedCart;
    } else {
      // If data is corrupt (not an array), clear it immediately
      console.warn("Corrupt cart data detected. Resetting cart.");
      localStorageRemoveItem("cartItems");
      return [];
    }
  } catch (error) {
    console.error("Failed to fetch cart from localStorage:", error);
    // On error (e.g. JSON parse error), also clear the bad data
    localStorageRemoveItem("cartItems");
    return [];
  }
};

const getInitialWishlist = () => {
  const userData = getParsedLocalStorageItem("userData");
  const token = localStorageGetItem("userToken");
  const isLoggedIn = !!userData && !!token;

  if (isLoggedIn) {
    return [];
  } else {
    try {
      // Retrieve the cart from local storage
      const storedWishlist = localStorageGetItem("wishlistItems");
      return storedWishlist ? JSON.parse(storedWishlist) : [];
    } catch (error) {
      console.error("Failed to fetch wishlist from localStorage:", error);
      return [];
    }
  }
};

const getInitialFormData = () => {
  const savedFormData = localStorageGetItem("formData");
  if (savedFormData) {
    // Parse the saved data
    const parsedSavedFormData = JSON.parse(savedFormData);
    // Return a new object merging the loaded data with default values for other form fields
    return {
      streetAddress: parsedSavedFormData.streetAddress || "",
      state: parsedSavedFormData.state || "",
      LGA: parsedSavedFormData.LGA || "",
      coordinates: [],
      phoneNumber: "",
      deliveryDate: "",
      deliveryFrequency: "",
      recipientName: "",
      landmark: parsedSavedFormData.landmark || "",
      bikeAllowed: parsedSavedFormData.bikeAllowed || "",
      bikeStartTime: parsedSavedFormData.bikeStartTime || "",
      bikeEndTime: parsedSavedFormData.bikeEndTime || "",
      whatsappNumber: parsedSavedFormData.whatsappNumber || "",
      isDefaultAddress: false,
      isSchoolOrBusinessAddress: false,
    };
  }

  return {
    streetAddress: "",
    state: "",
    LGA: "",
    coordinates: [],
    phoneNumber: "",
    deliveryDate: "",
    deliveryFrequency: "",
    recipientName: "",
    landmark: "",
    bikeAllowed: "",
    bikeStartTime: "",
    bikeEndTime: "",
    whatsappNumber: "",
    isDefaultAddress: false,
    isSchoolOrBusinessAddress: false,
  };
};

export const AppContextProvider = (props) => {
  // const navigate= useNavigate();
  const showToast = useCallback(
    ({ toastText, subText, isSuccess, type, closeToast }) => {
      const content = (
        <div>
          <h5 style={{ fontWeight: 700 }} className="my-auto">
            {toastText}
          </h5>
          {subText && <h6>{subText}</h6>}
        </div>
      );

      const toastOptions = {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        onClose: closeToast,
      };

      // ✅ NEW: explicit type
      if (type === "info") {
        toast.info(content, {
          ...toastOptions,
          style: {
            backgroundColor: "#fff",
            "--toastify-color-info": "#FFB800",
          },
          progressStyle: {
            background: "#FFB800", // Darker progress bar for visibility
          },
          icon: <FaInfoCircle size={24} color="#FFB800" />,
        });
        return;
      }

      if (type === "warning") {
        toast.info(content, {
          ...toastOptions,
          style: {
            backgroundColor: "#fff",
            "--toastify-color-info": "#2E90FA",
          },
          progressStyle: {
            background: "#2E90FA",
          },
          icon: <FaInfoCircle size={24} color="#2E90FA" />,
        });
        return;
      }

      if (isSuccess) {
        toast.success(content, toastOptions);
      } else {
        toast.error(content, toastOptions);
      }
    },
    [],
  );

  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showSourceModal, setShowSourceModal] = useState(false);
  const [showCart, setShowCart] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);

  const [showWishlist, setShowWishlist] = useState(false);
  const [discount, setDiscount] = useState(0);
  const [deliveryDiscount, setDeliveryDiscount] = useState(0);
  const [discountDescription, setDiscountDescription] = useState(false);
  const [promotionDescription, setPromotionDescription] = useState("");
  const [discountPercentage, setDiscountPercentage] = useState(0);
  const [percentageAmountCap, setPercentageAmountCap] = useState(0);
  const [discountType, setDiscountType] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 20,
    totalPages: 0,
    totalWishlistItems: 0,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [products, setProducts] = useState([]);
  const [searchVersion, setSearchVersion] = useState(0);
  const [activeCategories, setActiveCategories] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [cartItems, setCartItems] = useState(getInitialCart());
  const [wishlistItems, setWishlistItems] = useState(getInitialWishlist());
  const [apiWishlistItems, setApiWishlistItems] = useState([]);
  const [recentSearches, setRecentSearches] = useState([]);
  const [promoCode, setPromoCode] = useState("");
  const [loyaltyCode, setLoyaltyCode] = useState("");
  const [voucherCode, setVoucherCode] = useState("");
  const [voucherDiscount, setVoucherDiscount] = useState(0);
  const [minOrderAmount, setMinOrderAmount] = useState(0);
  const [productCategories, setProductCategories] = useState([]);

  const [formData, setFormData] = useState(getInitialFormData());
  const userData = getParsedLocalStorageItem("userData");
  const token = localStorageGetItem("userToken");
  const isLoggedIn = !!userData && !!token;
  // Effect to fetch user data if logged in

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 576);

  // useEffect(() => {
  //   const handleResize = () => {
  //     setIsMobile(window.innerWidth <= 576);
  //   };

  //   window.addEventListener('resize', handleResize);

  //   return () => {
  //     window.removeEventListener('resize', handleResize);
  //   };
  // }, []);

  useEffect(() => {
    let timeoutId = null;

    const handleResize = () => {
      clearTimeout(timeoutId); // Clear any previously set timeout
      timeoutId = setTimeout(() => {
        setIsMobile(window.innerWidth <= 576);
      }, 150); // Adjust debounce time as needed
    };

    window.addEventListener("resize", handleResize);

    // Cleanup the event listener on component unmount
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      const fetchUserDetails = async () => {
        try {
          const response = await axios.get(
            "https://production-api.virtuesshop.co/user",
            {
              headers: { "X-Auth-Token": token },
            },
          );

          localStorageSetItem("userData", JSON.stringify(response?.data?.data));

          setFormData((prevData) => ({
            ...prevData,
            _isAutoPopulation: true,
            streetAddress:
              response?.data?.data?.address?.streetAddress ||
              prevData.streetAddress,
            LGA: response?.data?.data?.address?.city?.name || prevData.LGA,
            state: response?.data?.data?.address?.state?.name || prevData.state,
          }));
        } catch (error) {
          console.error("Error fetching user details:", error);
          // showToast({ toastText: "An unexpected error occurred", isSuccess: false });
          let resMessage = "An unexpected error occurred"; // Default error message
          if (error.message === "Network Error") {
            resMessage =
              "Unable to connect. Please check your internet connection and try again.";
          } else if (
            error.message &&
            (error.message.includes("TIMED_OUT") ||
              error.message.includes("ERR_TIMED_OUT"))
          ) {
            resMessage =
              "Connection timed out. Please check your internet connection and try again.";
          } else if (
            error.message &&
            (error.message.includes("CONNECTION_CLOSED") ||
              error.message.includes("ERR_CONNECTION_CLOSED"))
          ) {
            resMessage =
              "Connection was closed unexpectedly. Please check your internet connection.";
          } else if (error.response) {
            const { status, data } = error.response;
            if ([400, 401, 405, 404, 409].includes(status)) {
              resMessage =
                data?.message ||
                error.message ||
                "An unexpected error occurred";
            } else if (status === 500) {
              resMessage =
                "We are experiencing technical issues. Please try again later.";
            }
          }

          if (error.response && error.response.status === 401) {
            localStorageRemoveItem("userData");
            localStorageRemoveItem("userToken");
            localStorageRemoveItem("totalWishlistItems");
            // navigate("/login");
            showToast({
              toastText: "Session timeout. Please log in again.",
              isSuccess: false,
            });
          } else if (
            error.response &&
            [400, 403, 404, 405, 409, 500].includes(error.response.status)
          ) {
            showToast({
              toastText: resMessage,
              isSuccess: false,
            });
          }
        }
      };
      fetchUserDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  useEffect(() => {
    localStorageSetItem(
      "formData",
      JSON.stringify({
        streetAddress: formData.streetAddress,
        state: formData.state,
        LGA: formData.LGA,
      }),
    );
  }, [formData.streetAddress, formData.state, formData.LGA]);

  useEffect(() => {
    const savedSearches =
      JSON.parse(localStorageGetItem("recentSearches")) || [];
    const lowerCaseMap = new Map();

    // Preserve the first occurrence of each search, case-insensitively
    savedSearches.forEach((search) => {
      const lowerCaseSearch = search.toLowerCase();
      if (!lowerCaseMap.has(lowerCaseSearch)) {
        lowerCaseMap.set(lowerCaseSearch, search);
      }
    });

    const uniqueSearches = Array.from(lowerCaseMap.values());
    setRecentSearches(uniqueSearches);
  }, []);
  useEffect(() => {
    try {
      if (cartItems.length > 0) {
        localStorageSetItem("cartItems", JSON.stringify(cartItems));
      }
    } catch (error) {
      console.error("Failed to save cart to localStorage:", error);
    }
  }, [cartItems]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        const storedCart = getInitialCart();
        if (storedCart) {
          setCartItems(storedCart);
        }
      }
    };

    // Add event listener for visibility change
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Cleanup event listener on component unmount
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  // useEffect(() => {
  //   localStorageSetItem('cartItems', JSON.stringify(cartItems));
  // }, [cartItems]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      localStorageSetItem("cartItems", JSON.stringify(cartItems));
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [cartItems]);

  useEffect(() => {
    localStorageSetItem("wishlistItems", JSON.stringify(wishlistItems));
  }, [wishlistItems]);

  const updateFormData = (newFormData) => {
    setFormData((prev) => ({ ...prev, ...newFormData }));
  };

  const handleShowWishlist = () => {
    setShowWishlist(true);
    // const contentId = userData ? userData?.id : 'Guest'; // Use user ID or a default for guests
    // const contentType = 'View Wishlist';
    // trackViewContent(contentId, contentType);
  };

  const handleCloseWishlist = () => setShowWishlist(false);

  const handleShowCart = () => {
    setShowCart(true);

    // const userData = getParsedLocalStorageItem('userData');
    // const contentId = userData ? userData?.id : 'Guest'; // Use user ID or a default for guests
    // const contentType = 'View Cart';

    // trackViewContent(contentId, contentType);
  };

  const handleCloseCart = () => {
    setShowCart(false);
  };

  const addToWishlist = (productId) => {
    const updatedWishlist = [...wishlistItems, productId];
    localStorageSetItem("wishlistItems", JSON.stringify(updatedWishlist));
    setWishlistItems(updatedWishlist);
  };

  const removeFromWishlist = (productId) => {
    const updatedWishlist = wishlistItems.filter((id) => id !== productId);
    localStorageSetItem("wishlistItems", JSON.stringify(updatedWishlist));
    setWishlistItems(updatedWishlist);
  };

  const updatedWishlist = (wishlist) => {
    localStorageSetItem("wishlistItems", JSON.stringify(wishlist));
  };

  const updateCart = useCallback((newCartItems) => {
    // Update cart items in AppContext
    setCartItems(newCartItems);

    localStorageSetItem("cartItems", JSON.stringify(newCartItems));
  }, []);

  const handleRemoveItem = (id) => {
    setCartItems((currentItems) =>
      currentItems.filter((item) => item.id !== id),
    );
  };

  const calculateTotalAmount = useCallback(() => {
    if (!Array.isArray(cartItems)) return 0;
    return cartItems.reduce(
      (acc, item) =>
        acc +
        ((item?.discountPrice ? item?.discountPrice : item?.price) ||
          item?.amount) *
          item?.quantity,
      0,
    );
  }, [cartItems]);

  const populateFormDataFromOrder = useCallback(
    (order) => {
      if (!order) return;

      setFormData((prev) => {
        const newFormData = { ...prev, _isAutoPopulation: true };
        const address = order.address || {};

        if (!newFormData.phoneNumber)
          newFormData.phoneNumber = order.phoneNumber || "";
        if (!newFormData.whatsappNumber)
          newFormData.whatsappNumber = order.whatsappNumber || "";
        if (!newFormData.streetAddress)
          newFormData.streetAddress = address.streetAddress || "";
        if (!newFormData.state) newFormData.state = address.state?.name || "";
        if (!newFormData.LGA) newFormData.LGA = address.city?.name || "";
        if (!newFormData.landmark)
          newFormData.landmark = address.landmark || "";

        if (!newFormData.bikeAllowed) {
          newFormData.bikeAllowed = order.isBikeAllowed ? "Yes" : "No";
        }

        if (!newFormData.bikeStartTime && order.bikeEntryTime) {
          const date = new Date(order.bikeEntryTime);
          newFormData.bikeStartTime = date
            .toLocaleTimeString("en-GB", {
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
              timeZone: "Africa/Lagos",
            })
            .slice(0, 5);
        }

        if (!newFormData.bikeEndTime && order.bikeExitTime) {
          const date = new Date(order.bikeExitTime);
          newFormData.bikeEndTime = date
            .toLocaleTimeString("en-GB", {
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
              timeZone: "Africa/Lagos",
            })
            .slice(0, 5);
        }

        return newFormData;
      });
    },
    [setFormData],
  );

  const contextValue = {
    showToast,
    cartItems,
    setCartItems,
    updateCart,
    handleRemoveItem,
    calculateTotalAmount,
    handleCloseCart,
    handleShowCart,
    setShowCart,
    showCart,
    showWishlist,
    setShowWishlist,
    handleCloseWishlist,
    handleShowWishlist,
    wishlistItems,
    setWishlistItems,
    addToWishlist,
    removeFromWishlist,
    updatedWishlist,
    apiWishlistItems,
    setApiWishlistItems,
    setShowSidebar,
    showSidebar,
    setShowLogoutModal,
    showLogoutModal,
    setShowShareModal,
    showShareModal,
    setSearchLoading,
    searchLoading,
    searchQuery,
    setSearchQuery,
    searchResults,
    setSearchResults,
    products,
    setProducts,
    pagination,
    setPagination,
    searchPerformed,
    setSearchPerformed,
    searchVersion,
    setSearchVersion,
    recentSearches,
    setRecentSearches,
    formData,
    setFormData,
    updateFormData,
    setDiscount,
    discount,
    promoCode,
    setPromoCode,
    discountDescription,
    percentageAmountCap,
    setPercentageAmountCap,
    setDiscountDescription,
    minOrderAmount,
    setMinOrderAmount,
    setDiscountType,
    discountType,
    promotionDescription,
    setPromotionDescription,
    setDiscountPercentage,
    discountPercentage,
    deliveryDiscount,
    setDeliveryDiscount,
    voucherCode,
    setVoucherCode,
    voucherDiscount,
    setVoucherDiscount,
    isMobile,
    setIsMobile,
    setShowSourceModal,
    showSourceModal,
    activeCategories,
    setActiveCategories,
    loyaltyCode,
    setLoyaltyCode,
    productCategories,
    setProductCategories,
    populateFormDataFromOrder,
  };

  return (
    <AppContext.Provider value={contextValue}>
      {props.children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  return useContext(AppContext);
};
